import React from 'react';
import { useTranslation } from 'react-i18next';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, FieldValues, useForm } from 'react-hook-form';

import { ReturnOptionProps } from '../../../../../../types/ProductFactory';
import { OptionProps } from '../../../components/SearchSelectInputV2';
import { AddressOption } from './AddressSearchSelectorV2';

import { Box, Button, VStack, Stack, useBoolean } from '@wegroup/design-system';
import AddressSearchInfo from './AddressSearchInfo';
import AddressSearchExtraButton from './AddressSearchExtraButton';
import TextInput from '../../../../../flows/src/components/input/advisory-flow-inputs/input/TextInput';

interface Props {
  handleOnCantFind: () => void;
  onSelected: (arg0: ReturnOptionProps) => void;
  loadOptions: (
    address: string,
    callback: (p: AddressOption[]) => void,
  ) => Promise<OptionProps[]> | void;
}

const AddressSearchQuickNL: React.FC<React.PropsWithChildren<Props>> = ({
  onSelected,
  loadOptions,
  handleOnCantFind,
}) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useBoolean();

  const searchAddress = (values: FieldValues) => {
    const { zipCode, houseNr } = values;

    setIsLoading.on();
    loadOptions(`${zipCode} ${houseNr}`, (foundAddresses) => {
      onSelected({ name: 'address', ...foundAddresses[0] });
      setIsLoading.off();
    });
  };

  const schema = yup.object({
    zipCode: yup
      .string()
      .required(t('Required'))
      .matches(/[1-9][0-9]{3} ?(S[BCE-RT-Z]|[A-RT-Z][A-Z])/, {
        message: t('invalidZipcode'),
      }),
    houseNr: yup.string().required(t('Required')),
  });

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
  });

  return (
    <VStack spacing="6" alignItems="stretch" mb="8" w="100%">
      <Stack
        spacing="2"
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        sx={{
          '> *': {
            flex: 1,
          },
        }}
      >
        <Controller
          name="zipCode"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextInput
              key="zipCode"
              error={error?.message}
              type="text"
              name="zipCode"
              onChange={(e) => onChange(e.currentTarget.value?.toUpperCase())}
              value={value}
            >
              {t(`Zipcode`)}
            </TextInput>
          )}
        />
        <Controller
          name="houseNr"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextInput
              key="houseNr"
              error={error?.message}
              type="text"
              name="houseNr"
              onChange={(e) => onChange(e.currentTarget.value)}
              value={value}
            >
              {t(`House no`)}
            </TextInput>
          )}
        />
      </Stack>
      <Stack
        spacing="4"
        justifyContent="space-between"
        direction={{
          base: 'column',
          tablet: 'row',
        }}
      >
        <AddressSearchInfo label={t('FLOWS_ADDRESS-SEARCH_NL_TIP')} />
        <Button
          isLoading={isLoading}
          w={{ base: '100%', tablet: 'unset' }}
          onClick={() => {
            handleSubmit(searchAddress)();
          }}
        >
          {t('Search')}
        </Button>
      </Stack>
      <Box alignSelf="center">
        <AddressSearchExtraButton
          onClick={handleOnCantFind}
          text={t("I can't find my address")}
        />
      </Box>
    </VStack>
  );
};

export default AddressSearchQuickNL;
